/**
 * Utilitário para formatação automática de números de telefone
 */
import { useState } from 'react';

/**
 * Remove todos os caracteres não numéricos do telefone
 * @param {string} phone - Número de telefone
 * @returns {string} - Apenas números
 */
export const cleanPhone = (phone) => {
  if (!phone) return '';
  return phone.replace(/\D/g, '');
};

/**
 * Formata um número de telefone para o padrão brasileiro (XX) XXXXX-XXXX
 * @param {string} phone - Número de telefone
 * @returns {string} - Telefone formatado
 */
export const formatPhone = (phone) => {
  if (!phone) return '';
  
  const cleaned = cleanPhone(phone);
  
  // Se tem 10 dígitos (telefone fixo): (XX) XXXX-XXXX
  if (cleaned.length === 10) {
    return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  
  // Se tem 11 dígitos (celular): (XX) XXXXX-XXXX
  if (cleaned.length === 11) {
    return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }
  
  // Se tem menos de 10 dígitos, retorna como está
  if (cleaned.length < 10) {
    return cleaned;
  }
  
  // Se tem mais de 11 dígitos, pega apenas os primeiros 11
  if (cleaned.length > 11) {
    const truncated = cleaned.substring(0, 11);
    return truncated.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }
  
  return cleaned;
};

/**
 * Valida se o telefone está no formato correto
 * @param {string} phone - Número de telefone
 * @returns {boolean} - True se válido
 */
export const isValidPhone = (phone) => {
  if (!phone || phone.trim() === '') return true; // Campo opcional
  
  const cleaned = cleanPhone(phone);
  return cleaned.length === 10 || cleaned.length === 11;
};

/**
 * Hook para formatação automática de telefone
 * @param {string} initialValue - Valor inicial
 * @returns {object} - { value, onChange, onBlur }
 */
export const usePhoneFormatter = (initialValue = '') => {
  const [value, setValue] = useState(initialValue);
  
  const handleChange = (event) => {
    const inputValue = event.target.value;
    const formatted = formatPhone(inputValue);
    setValue(formatted);
  };
  
  const handleBlur = () => {
    // Garante que o valor final está formatado corretamente
    const formatted = formatPhone(value);
    setValue(formatted);
  };
  
  return {
    value,
    onChange: handleChange,
    onBlur: handleBlur
  };
};

/**
 * Formata telefone durante a digitação (para uso em onChange)
 * @param {string} value - Valor atual do input
 * @returns {string} - Valor formatado
 */
export const formatPhoneOnInput = (value) => {
  // Remove caracteres não numéricos
  const cleaned = cleanPhone(value);
  
  // Aplica formatação baseada no tamanho
  if (cleaned.length <= 2) {
    return cleaned;
  } else if (cleaned.length <= 6) {
    return cleaned.replace(/(\d{2})(\d+)/, '($1) $2');
  } else if (cleaned.length <= 10) {
    return cleaned.replace(/(\d{2})(\d{4})(\d+)/, '($1) $2-$3');
  } else {
    return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }
};
